import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash';
import {
  dimensionRowLimitSelector,
  realTimeModeOnTrendsSelector,
  getFirstAppliedFilter,
  currentMetricsMetaSelector,
} from '@store/common/selectors';
import {
  allDimensionsListSelector,
  dimensionByTypesSelector,
} from '@store/common/dimensionList/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import {
  formatColumns,
  formatAppData,
  formatMultiColumns,
  formatMultiData,
} from '../dimension-table/utils';
import {
  chartIntervalSelector,
  noBrushSelector,
  tableCompareIntervalSelector,
} from '@store/trends/birdChart/selectors';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import {
  getMetricByUnit,
  getSecondaryOnlyMetricByKey,
  isSecondaryOnlyMetric,
} from '@utils/metrics/metricHelpers';
import useFetchGroupBy from './useFetchGroupBy';
import useSetDimensionExport from './useSetDimensionExport';
import {
  isTableCompareEnabledAtom,
  modeAtom,
  TABLE_MODES,
} from '@src/components/dimension-panel-hz/atoms';
import { isMetricTabActiveAtom, errorDataAtom } from '@src/components/metrics-panel-hz/atoms';
import { triggerDeletedFieldsError } from '@utils/errors/triggerPageError';
import { useSort } from './useSort';
import { useDimensionOperations } from './useDimensionOperations';
import { useCurrentFlowConfig } from '@src/hooks/flow/useFlowConfigs';
import { getFlowFilter } from '@utils/filter';
import { getAppFlowViewId } from '@utils/data-query/app-flow';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

/**
 * Annotation `value` emitted by IF-Server when a group-by merge returns zero rows after one or more
 * MGW subqueries failed (timeout, HTTP error, or `code !== 0`). Must stay in sync with
 * `GROUP_BY_EMPTY_AFTER_SUBQUERY_ERRORS` in `Instant-Filter-Server/.../groupby/orchestrator.ts`.
 */
const GROUP_BY_EMPTY_AFTER_SUBQUERY_ERRORS = 'GROUP_BY_EMPTY_AFTER_SUBQUERY_ERRORS';

/**
 * DPI / App Experience: network URL breakdown dimensions that are especially high-cardinality and
 * tend to fail or time out over long intervals when grouped (same class of issue as “endpoint”
 * not loading). Client uses this list for clearer copy when `GROUP_BY_EMPTY_AFTER_SUBQUERY_ERRORS`
 * is present; any other dimension still uses `dimensionMeta.isHighCardinality` from CCS metadata.
 */
const NETWORK_REQUEST_HIGH_CARDINALITY_GROUP_BYS = new Set([
  'network_request_url_host_path',
  'network_request_url_path',
]);

export const useAppCommonQueryParams = () => {
  const {
    orderedDimensionMetrics: { metrics, orderBy },
    dimensionMetrics,
  } = useDimensionOperations();
  const filter = useSelector(getFirstAppliedFilter);
  const interval = useSelector(chartIntervalSelector);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const selectedMetric = useSelector(selectedMetricSelector);
  const { limit } = useSelector(dimensionRowLimitSelector);
  const realTimeOnTrends = useSelector(realTimeModeOnTrendsSelector);
  const noBrushSelected = useSelector(noBrushSelector);

  return {
    // Used for api query
    queryMetrics: metrics,
    // Used for generating the table columns
    dimensionMetrics,
    filter,
    interval,
    metricFilter,
    sortByMetric: orderBy,
    limit,
    selectedMetric,
    realTimeOnTrends,
    noBrushSelected,
  };
};

function useAppDimension({
  dimension,
  metrics,
  orderBy,
  viewId,
  unit,
  enabled = true,
  disableDrilldown,
  doNotSort = false,
}) {
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const flowConfig = useCurrentFlowConfig();
  const { frustrationSignalId, flowId }: any = useDrilldownMeta();
  const {
    filter: commonFilter,
    selectedMetric,
    queryMetrics: commonQueryMetrics,
    sortByMetric: commonSortByMetric,
    dimensionMetrics: commonDimensionMetrics,
    interval,
    metricFilter,
    limit,
    realTimeOnTrends,
    noBrushSelected,
  }: any = useAppCommonQueryParams();

  const filter = getFlowFilter(commonFilter, flowConfig);
  // Only used for query
  const queryMetrics = metrics ?? commonQueryMetrics;
  // Only used for generating the table columns
  const dimensionMetrics = metrics ?? commonDimensionMetrics;
  const sortByMetric = orderBy ?? commonSortByMetric;
  const queryViewId = viewId ?? getAppFlowViewId(dimension, filter.rule ?? [], flowConfig);
  const errorData = useAtomValue(errorDataAtom);
  const dimensionByTypes: any = useSelector(dimensionByTypesSelector);
  const allDimensions: any[] = useSelector(allDimensionsListSelector);
  const dimensionMeta = allDimensions.find((d) => d.groupBy === dimension);

  const comparisonInterval: any = useSelector(tableCompareIntervalSelector);
  const isTableCompareEnabled = useAtomValue(isTableCompareEnabledAtom);

  const isMetricTabActive = useAtomValue(isMetricTabActiveAtom);
  const mode = useAtomValue(modeAtom);

  const { sortBy, onColumnSort } = useSort({ sortByMetric, dimension });
  const [searchText, onSearch] = useState('');

  // update local sort when selected or order by metric changes
  useEffect(() => {
    onColumnSort({ key: sortByMetric, order: 'desc' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByMetric, selectedMetric]);

  // not showActionColumn when disableDrilldown of this dimension
  const showActionColumn =
    !realTimeOnTrends && (isMetricTabActive || mode === TABLE_MODES.filter) && !disableDrilldown;

  const {
    isFetching,
    isError,
    error,
    data = {},
    refetch,
  } = useFetchGroupBy({
    dimension,
    metrics: queryMetrics,
    filter,
    interval,
    enabled,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    comparisonInterval,
    isTableCompareEnabled,
    viewId: queryViewId,
    doNotSort,
  });

  const { adjustedInterval, annotations }: any = data;

  // User-facing copy when IF returned 200 with empty rows but flagged upstream failure (see server).
  const groupByEmptyStateMessage = useMemo(() => {
    const hit = annotations?.find(
      (a: { value?: string }) => a?.value === GROUP_BY_EMPTY_AFTER_SUBQUERY_ERRORS,
    );
    if (!hit) {
      return null;
    }
    // Special-case copy for known network URL dimensions (see NETWORK_REQUEST_HIGH_CARDINALITY_GROUP_BYS).
    const isNetworkRequestUrlBreakdown = NETWORK_REQUEST_HIGH_CARDINALITY_GROUP_BYS.has(dimension);
    // All other dimensions: reuse CCS high-cardinality flag when set.
    if (isNetworkRequestUrlBreakdown || dimensionMeta?.isHighCardinality) {
      return 'This dimension can produce a very large number of distinct values. Over long intervals the breakdown query may fail or time out. Try a shorter date range or tighter filters.';
    }
    return 'The breakdown query did not return rows (upstream error or timeout). Try a shorter date range or narrower filters.';
  }, [annotations, dimension, dimensionMeta?.isHighCardinality]);

  useEffect(() => {
    triggerDeletedFieldsError(annotations);
  }, [annotations]);

  const { rows, totals, columns, pctShareMaxValues } = useMemo(() => {
    let rows = [];
    let totals = {};
    let columns = [];
    let pctShareMaxValues = {};

    // unit based metrics
    const columnMetrics = dimensionMetrics.map((metric) => {
      return getMetricByUnit(unit, metric);
    });

    const removeSecondaryOnlyMetrics = columnMetrics.filter(
      (metric) => !isSecondaryOnlyMetric({ metricKey: metric }),
    );

    const reorderedColumnMetrics = removeSecondaryOnlyMetrics.reduce(
      (acc, metric) => {
        const secondaryOnlyMetric = getSecondaryOnlyMetricByKey({
          metricKey: metric,
          defaultValue: null,
        });
        acc.push(metric);
        if (secondaryOnlyMetric && !acc.includes(secondaryOnlyMetric)) {
          acc.push(secondaryOnlyMetric);
        }
        return acc;
      },
      <string[]>[],
    );

    if (!isEmpty(dimensionByTypes)) {
      if ('metricFilterData' in data) {
        ({ rows, totals, pctShareMaxValues } = formatMultiData({
          data,
          sortBy,
          searchText,
          metrics: reorderedColumnMetrics,
        }));
        columns = formatMultiColumns({
          dimension,
          metrics: reorderedColumnMetrics,
          showNAForMetric: null,
          showActionColumn,
          showTrend: isTableCompareEnabled,
          errorData,
          flowId,
          frustrationSignalId,
        });
      } else {
        ({ rows, totals, pctShareMaxValues } = formatAppData({
          data,
          sortBy,
          searchText,
          flowId,
          frustrationSignalId,
        }));
        columns = formatColumns({
          dimension,
          metrics: reorderedColumnMetrics,
          showNAForMetric: null,
          showActionColumn,
          showTrend: isTableCompareEnabled,
          errorData,
          disableDrilldown,
          showHeaderTooltip: !!flowId,
          flowId,
          frustrationSignalId,
        });
      }
    }

    return { rows, totals, columns, pctShareMaxValues };
  }, [
    data,
    dimension,
    dimensionByTypes,
    currentMetricsMeta,
    showActionColumn,
    searchText,
    sortBy,
    unit,
    errorData,
    disableDrilldown,
    isTableCompareEnabled,
    dimensionMetrics,
  ]);

  useSetDimensionExport({
    dimension,
    metrics: dimensionMetrics.filter((m) => !errorData[m]),
    data: rows,
    totals: totals,
    showNAForMetric: null,
  });

  return {
    isFetching,
    isError,
    error,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals,
    data: rows,
    sortBy,
    onColumnSort,
    onSearch,
    searchText,
    adjustedInterval,
    pctShareMaxValues,
    metrics: dimensionMetrics,
    groupByEmptyStateMessage,
  };
}

export default useAppDimension;
